import React, { useEffect, useState } from "react";
import { View, Text, StyleSheet } from "react-native";
import { Doughnut } from "react-chartjs-2";
import "chart.js";
import { ScrollView } from "react-native-gesture-handler";
import { useSelector } from "react-redux";
import Spinner from "../../../components/shared/Spinner";

type ChartCardProps = {
  title: string;
  data: number[];
  labels: string[];
  colors: string[];
};

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  data,
  labels,
  colors,
}) => {
  const chartData = {
    labels: labels,
    datasets: [
      {
        data: data,
        backgroundColor: colors,
      },
    ],
  };

  return (
    <View style={styles.chartContainer}>
      <Text style={styles.title}>{title}</Text>
      <View style={styles.chartWrapper}>
        <Doughnut
          data={chartData}
          options={{ maintainAspectRatio: false, legend: { display: false } }}
        />
      </View>
      <View style={styles.legend}>
        {labels.map((label, index) => {
          return (
            <>
              <View key={index} style={styles.legendItem}>
                <View
                  style={[
                    styles.legendColor,
                    { backgroundColor: colors[index] },
                  ]}
                />
                <Text style={styles.label}>{`${data[index]}  ${label}`}</Text>
              </View>
              <View style={styles.horizontalLine} />
            </>
          );
        })}
      </View>
    </View>
  );
};

const ItemTrackerDashboard = () => {
  const [loading, setLoading] = useState<boolean>();
  const [orderStatusData, setOrderStatusData] = useState<number[]>([]);
  const [orderStatesData, setOrderStatesData] = useState<number[]>([]);
  const [last3MonthsData, setLast3MonthsData] = useState<number[]>([]);

  const { orders } = useSelector((state: any) => state.productTracker);

  const trackTotalStatusOrders = (status: string) => {
    return orders?.filter((order: any) => order.OrderStatus === status).length;
  };

  const trackTotalStatesOrders = (state: string) => {
    return orders?.filter(
      (order: any) =>
        order?.StateDelivery?.toString()?.toLowerCase() ===
        state.toString().toLowerCase()
    ).length;
  };

  const track3MonthsData = (month: string): number => {
    if (!orders || !Array.isArray(orders)) return 0;

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return orders.filter((order: any) => {
      if (!order?.ETA) return 0;
      const etaDate = new Date(order.ETA);
      if (isNaN(etaDate.getTime())) return 0;
      const etaMonthName = monthNames[etaDate.getMonth()];
      return etaMonthName === month;
    }).length;
  };

  const getLast3Months = (): string[] => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const today = new Date();
    const last3Months: string[] = [];

    for (let i = 0; i < 3; i++) {
      const monthIndex = (today.getMonth() - i + 12) % 12;
      last3Months.push(monthNames[monthIndex]);
    }

    return last3Months;
  };

  useEffect(() => {
    setLoading(true);

    // order status data
    setOrderStatusData([
      trackTotalStatusOrders("Onhold"),
      trackTotalStatusOrders("New Order"),
      trackTotalStatusOrders("Arrived"),
      trackTotalStatusOrders("Shipped"),
      trackTotalStatusOrders("Rush Order"),
      trackTotalStatusOrders("Order Placed"),
      trackTotalStatusOrders("At Risk"),
    ]);

    // order state data
    setOrderStatesData([
      trackTotalStatesOrders("Mel"),
      trackTotalStatesOrders("NSW"),
      trackTotalStatesOrders("Perth"),
      trackTotalStatesOrders("Qld"),
      trackTotalStatesOrders("Tas"),
    ]);

    // last 3 months data
    const last3Months = getLast3Months();
    setLast3MonthsData([
      track3MonthsData(last3Months[0]),
      track3MonthsData(last3Months[1]),
      track3MonthsData(last3Months[2]),
    ]);

    setLoading(false);
  }, [orders]);

  return (
    <ScrollView style={styles.container}>
      {loading ? (
        <Spinner
          size="small"
          color="#74BBFF"
          styles={{ flexDirection: "row" }}
        />
      ) : (
        <View style={styles.grid}>
          <ChartCard
            title="Orders"
            data={orderStatusData}
            labels={[
              "Onhold",
              "New Order",
              "Arrived",
              "Shipped",
              "Rush Order",
              "Order Placed",
              "At Risk",
            ]}
            colors={[
              "#000000",
              "#0650CF",
              "#DC4545",
              "green",
              "purple",
              "grey",
              "#D9D9D6",
            ]}
          />
          <ChartCard
            title="States"
            data={orderStatesData}
            labels={["Mel", "NSW", "Perth", "Qld", "Tas"]}
            colors={["#AE6E00", "#000000", "#DC4545", "#0650CF", "#D9D9D9"]}
          />
          <ChartCard
            title="Order Due"
            data={last3MonthsData}
            labels={getLast3Months()}
            colors={["#DC4545", "#0650CF", "#D9D9D9"]}
          />
          <ChartCard
            title="Product Categories"
            data={[30, 320, 24]}
            labels={["Fresh Produce", "Meat & Seafood", "Ready Meal"]}
            colors={["#D9D9D9", "#DC4545", "#0650CF"]}
          />
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: "#fff",
    display: "flex",
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  chartContainer: {
    marginBottom: 20,
    alignItems: "center",
    width: 402,
    height: 442,
    borderWidth: 1,
    borderColor: "#fff",
  },
  chartWrapper: {
    width: 402,
    height: 203,
    backgroundColor: "#f4f4f4",
  },
  title: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 10,
    textAlign: "left",
    alignContent: "flex-start",
  },
  label: {
    fontSize: 15,
    fontWeight: 700,
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: 5,
  },
  legend: {
    marginTop: 10,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 5,
  },
  legendColor: {
    width: 10,
    height: 34,
    marginRight: 5,
  },
  horizontalLine: {
    height: 1,
    backgroundColor: "#ccc",
    width: 153,
  },
});

export default ItemTrackerDashboard;
